import { useEffect } from "react";
import { useSocketContext } from "../context/SocketContext";

/** Compat legado (id puro) e payload enriquecido `{ id, notify }`. */
export function parseConversationSocketPayload(raw) {
  if (raw == null) return { conversationId: null, notify: true };
  if (typeof raw === "object") {
    const id = raw.id ?? raw.conversationId ?? null;
    return { conversationId: id != null ? String(id) : null, notify: raw.notify !== false };
  }
  if (typeof raw === "number") {
    return { conversationId: String(raw), notify: true };
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed.startsWith("{")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === "object") {
          const id = parsed.id ?? parsed.conversationId ?? null;
          return {
            conversationId: id != null ? String(id) : null,
            notify: parsed.notify !== false,
          };
        }
      } catch {
        /* legado */
      }
    }
    return { conversationId: trimmed, notify: true };
  }
  return { conversationId: String(raw), notify: true };
}

/**
 * Hook que escuta eventos do WebSocket e aciona refetch do useConversationDetails e useConversations.
 */
export const useRealtimeMessages = ({
  selectedConversationId,
  refetchConversationDetails,
  refetchConversations,
  refreshConversationMessages,
  onSocketActivity,
}) => {
  const { socket } = useSocketContext();

  useEffect(() => {
    if (!socket) return;

    const refreshForConversation = (id) => {
      if (refetchConversations) refetchConversations();

      if (
        selectedConversationId != null &&
        String(id) === String(selectedConversationId)
      ) {
        if (refetchConversationDetails) refetchConversationDetails();
        if (refreshConversationMessages) refreshConversationMessages(String(id));
      }
    };

    const handleMessage = (raw) => {
      const { conversationId, notify } = parseConversationSocketPayload(raw);
      if (!conversationId) return;
      refreshForConversation(conversationId);
      if (onSocketActivity) {
        onSocketActivity({ eventName: "message", conversationId, notify });
      }
    };

    const handleRequest = (raw) => {
      const { conversationId, notify } = parseConversationSocketPayload(raw);
      if (!conversationId) return;
      refreshForConversation(conversationId);
      if (onSocketActivity) {
        onSocketActivity({ eventName: "request", conversationId, notify });
      }
    };

    const handleReload = (raw) => {
      const { conversationId } = parseConversationSocketPayload(raw);
      if (!conversationId) return;
      refreshForConversation(conversationId);
      if (onSocketActivity) {
        onSocketActivity({ eventName: "messagesReload", conversationId, notify: false });
      }
    };

    socket.on("message", handleMessage);
    socket.on("messagesReload", handleReload);
    socket.on("request", handleRequest);

    return () => {
      socket.off("message", handleMessage);
      socket.off("messagesReload", handleReload);
      socket.off("request", handleRequest);
    };
  }, [
    socket,
    selectedConversationId,
    refetchConversationDetails,
    refetchConversations,
    refreshConversationMessages,
    onSocketActivity,
  ]);
};
